import {
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  FormLabel,
} from "@material-ui/core";
import React from "react";
import styled from "styled-components";
import theme from "../../../assets/theme";
import { Formik } from "formik";
import { PrimaryCTAButton } from "../../common/Buttons";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import FormField from "../../common/FormField/FormField";
import ViewerService from "../../../services/ViewerService";
import { CustomInputField } from "../../common/FormInputs";
import COLORS from "../../../assets/Colors";
import { chatModalValidationSchema } from "../../../helpers/validationSchema";
import _ from "lodash";
import {navigate} from "@reach/router";
const customOrder = ["fullName", "email", "clientId"];

const typeMapping = {
  email: "email",
  clientId: "number",
  fullName: "text",
  label: "text",
};

const ChatModal = ({ modal, setModal, communicateFields, eventId }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const themes = useTheme();
  const isBigScreen = useMediaQuery(themes.breakpoints.up(640));

  const initialState = {
    fullName: "",
    clientId: "",
    email: "",
    label: "",
  };

  async function onFormSubmit(values) {
    let stayOnModal = false;
    try {
      if(!Boolean(values?.messageText)){
        enqueueSnackbar("Text question is empty", {
          variant: "error",
        });
        stayOnModal = !stayOnModal;
        return;
      }
      setLoader({ state: true, message: "Ask questions..." });
      const { email, fullName, clientId, label } = values?.items[0];
      const payload = {
        eventId: eventId,
        messageText: values?.messageText,
        customFields: {
          fullName: fullName,
          emailId: email,
          dp_client_id: clientId,
          label: label,
        },
      };
      const response = await ViewerService.metaCommunicateQuestions(payload);
      if (response) {
        enqueueSnackbar("Question sent successfully", {
          variant: "success",
        });
      }
      navigate(`#home`);
    } catch (err) {
      const message = getErrorMessage(err);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
        if(!stayOnModal){
          setLoader({ state: false });
          setModal({ state: false });
        }
    }
  }

  return (
    <DrawerContainer>
      <Container>
        <FormContainer>
          <Formik
            initialValues={{
              messageText: "",
              items: communicateFields?.length
                ? communicateFields.map((item) => {
                  const filtered = {};

                  // Include label if it's a non-empty string
                  if (item.label && item.label.length > 0) {
                    filtered.label = item.label;
                  }

                  // Check for boolean true values
                  ["email", "clientId", "fullName"].forEach((key) => {
                    if (item[key] === true) {
                      filtered[key] = "";
                    }
                  });

                  return filtered;
                })
                : [initialState] || [initialState],
            }}
            enableReinitialize
            validateOnBlur
            validationSchema={chatModalValidationSchema}
            validateOnChange
            onSubmit={onFormSubmit}
          >
            {({
              values,
              handleChange,
              errors,
              handleSubmit,
              touched,
              setFieldValue,
              setFieldError,
              handleBlur,
            }) => (
              <form>
                {values.items.map((arr, index) => (
                  <Paper
                    elevation={3}
                    style={{
                      padding: 16,
                      border: "1px solid #e1e3ee",
                      marginTop: "15px",
                      marginBottom: "15px",
                    }}
                  >
                    <Grid
                      container
                      item
                      xs={12}
                      spacing={3}
                      style={{ padding: 16 }}
                    >
                      {Object.entries(communicateFields[0])
                        .filter(([key, value]) => {
                          if (key === "label") return value !== "";
                          return value === true;
                        })
                        .sort(([keyA], [keyB]) => {
                          const indexA = customOrder.indexOf(keyA);
                          const indexB = customOrder.indexOf(keyB);

                          // If key not found in customOrder, treat it like very large index
                          return (
                            (indexA === -1 ? 999 : indexA) -
                            (indexB === -1 ? 999 : indexB)
                          );
                        })
                        .map(([key, value]) => ({
                          label: _.startCase(key),
                          name: key,
                          type: typeMapping[key] || "text",
                          placeholder: `Enter ${_.startCase(key)}`,
                        }))
                        .map((item, idx) => (
                          <Grid
                            item
                            xs={12}
                            key={`items[${index}].${item.name}`}
                            style={styles.gridItem}
                          >
                            <div
                              className="form-fields"
                              style={{ width: "100%", paddingTop: 5 }}
                            >
                              <FormLabel
                                style={{
                                  fontFamily: theme.fonts.primaryFontSemiBold,
                                  fontSize: 13,
                                  color: COLORS.INPUT_LABEL,
                                  display: item?.disabled ? "none" : "",
                                }}
                              >
                                {item.label}
                              </FormLabel>
                              <div className="disabledArrows">
                                <CustomInputField
                                  className="input-white"
                                  row={4}
                                  type={item.type}
                                  value={arr[item.name]}
                                  id={item.name}
                                  placeholder={item.placeholder}
                                  name={`items[${index}].${item.name}`}
                                  error={
                                    `items[${index}].${item.name}` &&
                                    errors?.items?.length &&
                                    errors?.items[index] !== undefined &&
                                    errors?.items[index][item.name]
                                  }
                                  helperText={
                                    `items[${index}].${item.name}` &&
                                    errors?.items?.length &&
                                    errors?.items[index] !== undefined &&
                                    errors?.items[index][item.name]
                                  }
                                  variant="outlined"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  style={{
                                    marginBottom: isBigScreen ? 0 : 10,
                                    display: item.disabled ? "none" : "",
                                  }}
                                />
                              </div>
                            </div>
                          </Grid>
                        ))}
                    </Grid>
                  </Paper>
                ))}
                <div style={{ marginTop: 17, display: "flex" }}>
                  <div style={{ width: "100%" }}>
                    <FormField
                      input={{
                        name: "messageText",
                        label: "Question",
                        type: "textarea",
                        placeholder: "Type your question here",
                      }}
                      labelStyle={{ fontSize: "18px", fontWeight: "bold" }}
                      style={{ backgroundColor: "#f9f9f9" }}
                      multiline
                      maxLength={0}
                      {...{
                        touched,
                        errors,
                        values,
                        handleChange,
                        handleBlur,
                      }}
                    />
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <PrimaryCTAButton
                    style={{
                      marginLeft: "10px",
                      marginTop: "30px",
                      width: "90px",
                      height: "15px",
                      padding: "10px 10px",
                    }}
                    onClick={handleSubmit}
                  >
                    SEND
                  </PrimaryCTAButton>
                  <PrimaryCTAButton
                    style={{
                      background: "#d91b1b",
                      border: "1px solid #d91b1b",
                      marginLeft: "10px",
                      marginTop: "30px",
                      width: "90px",
                      height: "15px",
                      padding: "10px 10px",
                    }}
                    onClick={() => setModal({ state: false })}
                  >
                    CANCEL
                  </PrimaryCTAButton>
                </div>
              </form>
            )}
          </Formik>
        </FormContainer>
      </Container>
    </DrawerContainer>
  );
};

const styles = {
  container: {
    backgroundColor: "#A9A9A9", // gray background
    padding: "20px",
    borderRadius: "8px",
  },
  gridItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "5px",
  },
  label: {
    fontWeight: "bold",
  },
};

export default ChatModal;

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

// const DrawerContainer = styled.div`
//   width: 100%;
//   position: absolute;
//   top: 50%;
//   left: 50%;
//   transform: translate(-50%, -50%);
//   @media ${theme?.breakpoints?.sm_up} {
//     max-width: 30%;
//     top: 40%;
//     left: 20%;
//   }
// `;
const DrawerContainer = styled.div`
  position: fixed;         /* fixed to viewport, not relative to parent */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);  /* truly centers the element */
  width: 100%;
  max-width: 1000px;        /* limit width */
  //padding: 1rem;
  background: white;
   max-height:100vh;
   height:75vh;
  @media ${theme?.breakpoints?.sm_up} {
   // max-width: 30%;
  }
`;


const FormContainer = styled.p`
 // padding-top: 14px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 10px;
  & form {
    padding-top: 10px;
  }
`;